import axios from "axios";
import { API_BASE_URL } from "../config";

const api = axios.create({
    baseURL: API_BASE_URL,
});

// Request interceptor: Attach accessToken to every outgoing request
api.interceptors.request.use((config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor: Handle errors (401, 403) and implement token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // 1. Handle 401 Unauthorized (Expired or Invalid Access Token)
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem("refreshToken");

            if (refreshToken) {
                try {
                    // Attempt to get a new access token using the refresh token
                    const refreshRes = await axios.post(`${API_BASE_URL}token/refresh/`, {
                        refresh: refreshToken
                    });

                    if (refreshRes.status === 200) {
                        const newAccess = refreshRes.data.access;
                        localStorage.setItem("accessToken", newAccess);
                        
                        // Update the authorization header and retry original request
                        api.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
                        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;
                        return api(originalRequest);
                    }
                } catch (refreshError) {
                    console.error("Token refresh failed. Redirecting to login...", refreshError);
                    // Refresh token is also invalid/expired - full logout
                    handleLogout();
                }
            } else {
                handleLogout();
            }
        }

        // 2. Handle 403 Forbidden (Missing Permissions)
        if (error.response && error.response.status === 403) {
            console.warn("Access Forbidden: Syncing RBAC and Redirecting...");
            localStorage.removeItem("navigation"); // Invalidate cache
            window.dispatchEvent(new Event("rbac_update")); // Tell AuthContext to resync
            
            if (!window.location.pathname.includes('/dashboard')) {
                window.location.href = '/dashboard';
            }
        }

        return Promise.reject(error);
    }
);

/**
 * Helper to clear local state and redirect to login
 */
function handleLogout() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("isAuth");
    localStorage.removeItem("navigation");
    
    // Redirect only if not already on the login page
    if (window.location.pathname !== "/" && window.location.pathname !== "/login") {
        window.location.href = "/";
    }
}

export default api;
